import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { useOrganization } from "../context/OrganizationContext";
import { UpsellBanner } from "../components/Upsell";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { ChevronLeft, ChevronRight, FileText, User, ArrowLeftRight, Building2, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const actionIcons = {
  customer: User,
  operation: ArrowLeftRight,
  organization: Building2,
  beneficial_owner: Shield,
  user: User,
};

export default function AuditLogs() {
  const { hasFeature, hasPermission, getRequiredPlan } = useOrganization();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [resourceFilter, setResourceFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const hasAuditFeature = hasFeature("bitacora_audit_log");
  const hasAuditPermission = hasPermission("view_audit_logs");

  const fetchLogs = useCallback(async () => {
    if (!hasAuditFeature || !hasAuditPermission) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: 50 };
      if (resourceFilter !== "all") params.resource_type = resourceFilter;
      const res = await axios.get(`${API}/audit-logs`, { params, withCredentials: true });
      setLogs(res.data.items);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail?.error === "feature_unavailable") {
        setError("feature_unavailable");
      } else {
        setLogs([]);
      }
    } finally {
      setLoading(false);
    }
  }, [page, resourceFilter, hasAuditFeature]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Show upsell if feature not available OR if user doesn't have permission
  if (!hasAuditFeature || !hasAuditPermission || error === "feature_unavailable") {
    return (
      <Layout>
        <div data-testid="audit-logs-page">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white" data-testid="audit-logs-heading">Bitácora de Auditoría</h1>
            <p className="text-slate-400 mt-1">Registro inmutable de todas las actividades</p>
          </div>
          <UpsellBanner 
            feature="bitacora_audit_log" 
            requiredPlan={getRequiredPlan("bitacora_audit_log")} 
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="audit-logs-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white" data-testid="audit-logs-heading">Bitacora de Auditoria</h1>
          <p className="text-slate-400 mt-1">{total} registros — Registro inmutable de actividades</p>
        </div>

        <div className="flex gap-3 mb-6">
          <Select value={resourceFilter} onValueChange={(v) => { setResourceFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700 text-slate-300" data-testid="resource-filter">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="user">Usuarios</SelectItem>
              <SelectItem value="customer">Clientes</SelectItem>
              <SelectItem value="operation">Operaciones</SelectItem>
              <SelectItem value="organization">Organizaciones</SelectItem>
              <SelectItem value="beneficial_owner">Propietarios</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl" data-testid="audit-logs-table">
          <div className="divide-y divide-slate-800">
            {loading ? (
              <div className="px-5 py-12 text-center">
                <div className="w-6 h-6 border-2 border-teal-400 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : logs.length === 0 ? (
              <div className="px-5 py-12 text-center text-slate-500">No hay registros</div>
            ) : logs.map((log) => {
              const Icon = actionIcons[log.resource_type] || FileText;
              return (
                <div key={log.log_id} className="px-5 py-3 flex items-center gap-4 hover:bg-slate-800/30 transition-colors" data-testid={`log-${log.log_id}`}>
                  <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                    <Icon size={16} className="text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-200">{log.user_name || "Sistema"}</span>
                      <span className="text-sm text-slate-400">{log.action}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">{log.resource_type}</span>
                      {log.resource_id && (
                        <span className="text-xs text-slate-500 font-mono">{log.resource_id}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 flex-shrink-0">
                    {log.created_at ? new Date(log.created_at).toLocaleString("es-MX") : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-slate-500">Pagina {page} de {pages} ({total} registros)</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border-slate-700 text-slate-400">
                <ChevronLeft size={16} />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} className="border-slate-700 text-slate-400">
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
